import { useEffect, useRef, useState } from "react";
import { History, X } from "lucide-react";
import { useCustomerAuth } from "@/context/AuthContext";
import { PocketPreview } from "@/components/PocketPreview";

const MAX = 3;

function storageKey(userId) {
  return `recent_pockets_${userId || "guest"}`;
}

function readPockets(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.slice(0, MAX) : [];
  } catch { return []; }
}

function writePockets(userId, list) {
  try { localStorage.setItem(storageKey(userId), JSON.stringify(list.slice(0, MAX))); } catch { /* ignore */ }
}

function _label(p) {
  const parts = [];
  if (p.genre && p.genre !== "All") parts.push(p.genre);
  if (p.bpm && p.bpm !== "all") {
    if (p.bpm === "lt80") parts.push("<80 BPM");
    else if (p.bpm === "gt170") parts.push("170+ BPM");
    else parts.push(`${p.bpm} BPM`);
  }
  if (p.key && p.key !== "all") parts.push(p.key);
  return parts.join(" · ");
}

function isAnyFilter(p) {
  return (p.genre && p.genre !== "All") || (p.bpm && p.bpm !== "all") || (p.key && p.key !== "all");
}

/**
 * Hook: records the signed-in artist's current pocket into their local history
 * (last 3 unique combos, no repeats, "All/all/all" ignored). Debounced so a user
 * clicking through chips doesn't fill the list with transient states.
 */
export function useRecordRecentPocket(genre, bpm, key) {
  const { user } = useCustomerAuth();
  useEffect(() => {
    if (!user || user === false || user === null) return;
    const p = { genre, bpm, key };
    if (!isAnyFilter(p)) return;
    const uid = user.user_id || user.id || user.email;
    const t = setTimeout(() => {
      const current = readPockets(uid);
      // Dedupe on genre+bpm+key regardless of order.
      const next = [p, ...current.filter((x) => !(x.genre === p.genre && x.bpm === p.bpm && x.key === p.key))];
      writePockets(uid, next);
      window.dispatchEvent(new Event("recent-pockets-updated"));
    }, 1200);
    return () => clearTimeout(t);
  }, [user, genre, bpm, key]);
}

/**
 * The visible chip row. Hidden for guests and when there's no history yet.
 * Tapping a chip hands the pocket to the parent so filters + URL apply.
 */
export function RecentPockets({ onApply }) {
  const { user } = useCustomerAuth();
  const [pockets, setPockets] = useState([]);
  const [hovered, setHovered] = useState(null);
  const chipRefs = useRef({});

  useEffect(() => {
    if (!user || user === false || user === null) { setPockets([]); return; }
    const uid = user.user_id || user.id || user.email;
    const load = () => setPockets(readPockets(uid));
    load();
    const onUpdate = () => load();
    window.addEventListener("recent-pockets-updated", onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener("recent-pockets-updated", onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, [user]);

  if (!user || user === false || !pockets.length) return null;

  const clearAll = () => {
    const uid = user.user_id || user.id || user.email;
    writePockets(uid, []);
    setPockets([]);
    setHovered(null);
    window.dispatchEvent(new Event("recent-pockets-updated"));
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="recent-pockets">
      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        <History size={11} /> Recent
      </span>
      {pockets.map((p, i) => {
        const refKey = `${p.genre}-${p.bpm}-${p.key}`;
        if (!chipRefs.current[refKey]) chipRefs.current[refKey] = { current: null };
        const ref = chipRefs.current[refKey];
        return (
          <button
            key={refKey}
            ref={(el) => { ref.current = el; }}
            type="button"
            onClick={() => onApply?.(p)}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
            onFocus={() => setHovered(i)}
            onBlur={() => setHovered((h) => (h === i ? null : h))}
            data-testid={`recent-pocket-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#141419] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-emerald-300/40 hover:text-emerald-200"
          >
            {_label(p) || "Pocket"}
          </button>
        );
      })}
      <button
        type="button"
        onClick={clearAll}
        aria-label="Clear recent pockets"
        data-testid="recent-pockets-clear"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors hover:border-red-400/40 hover:text-red-300"
        title="Clear recent pockets"
      >
        <X size={11} />
      </button>
      {hovered !== null && pockets[hovered] && (
        <PocketPreview
          anchorRef={chipRefs.current[`${pockets[hovered].genre}-${pockets[hovered].bpm}-${pockets[hovered].key}`]}
          search={pockets[hovered]}
          onClose={() => setHovered(null)}
        />
      )}
    </div>
  );
}
